import { MedplumClient, parseReference, resolveId } from "@medplum/core";
import { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Questionnaires } from "@/fhir/enums/questionnaires";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

export const fetchClientProfileQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<[Questionnaire | undefined, QuestionnaireResponse | undefined]> => {
  const questionnaireResponse = await fetchLatestClientIntakeResponse(medplum, clientId);

  const { bundle: questionnaireBundle } = await medplum.searchResources("Questionnaire", {
    url: Questionnaires.ClientIntake,
    ...(questionnaireResponse?.questionnaire
      ? { _id: parseReference({ reference: questionnaireResponse.questionnaire })[1] }
      : { status: "active" }),
  });

  const questionnaire = getResourcesFromBundle("Questionnaire", questionnaireBundle)[0];

  return [questionnaire, questionnaireResponse];
};

/**
 * Loads the intake version a client's latest intake response was answered on.
 *
 * @param medplum - the Medplum client.
 * @param clientId - the client's Patient id.
 * @returns the Questionnaire that response points to, or undefined when the client has no intake
 *   response.
 */
export const fetchClientIntakeQuestionnaire = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<Questionnaire | undefined> => {
  const questionnaireResponse = await fetchLatestClientIntakeResponse(medplum, clientId);
  if (!questionnaireResponse?.questionnaire) return undefined;

  return medplum.searchOne("Questionnaire", {
    url: Questionnaires.ClientIntake,
    _id: resolveId({ reference: questionnaireResponse.questionnaire }),
  });
};

/**
 * Loads the client's most recently updated intake QuestionnaireResponse.
 *
 * @param medplum - the Medplum client.
 * @param clientId - the client's Patient id.
 * @returns the latest intake response, or undefined when the client has none.
 */
const fetchLatestClientIntakeResponse = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<QuestionnaireResponse | undefined> => {
  const { bundle } = await medplum.searchResources("QuestionnaireResponse", {
    patient: joinReferenceString("Patient", clientId),
    identifier: Questionnaires.ClientIntake,
    _sort: "-_lastUpdated",
    _count: 1,
  });

  return getResourcesFromBundle("QuestionnaireResponse", bundle)?.[0];
};
